class AveyProductGallery extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';
    this.media = [...this.querySelectorAll('[data-media-id]')];
    this.stage = this.querySelector('.avey-product-gallery__stage');
    this.viewport = this.querySelector('.avey-product-gallery__viewport');
    this.counter = this.querySelector('[data-gallery-counter]');
    this.previousButton = this.querySelector('[data-gallery-previous]');
    this.nextButton = this.querySelector('[data-gallery-next]');
    this.activeIndex = Math.max(0, this.media.findIndex((media) => !media.hidden));
    this.preloadedMedia = new Map();
    this.isTransitioning = false;
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.desktopMediaQuery = window.matchMedia('(min-width: 1024px)');
    this.hoverZoomMediaQuery = window.matchMedia('(min-width: 1024px) and (hover: hover) and (pointer: fine)');
    this.nativeScrollFrame = null;
    this.nativeScrollTimer = null;
    this.nativeScrollTargetIndex = null;
    this.zoomDialog = null;
    this.zoomItems = [];
    this.zoomIndex = 0;
    this.zoomPreloads = new Map();
    this.zoomPreloadGeneration = 0;
    this.zoomRenderGeneration = 0;
    this.hoverZoomPane = null;
    this.hoverZoomLens = null;
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeyDown);
    this.addEventListener('focusin', this.onFocusIn);
    this.addEventListener('pointerdown', this.onPointerDown, { passive: true });
    this.desktopMediaQuery.addEventListener('change', this.onDesktopMediaChange);
    this.viewport?.addEventListener('scroll', this.onNativeViewportScroll, { passive: true });
    this.viewport?.addEventListener('scrollend', this.onNativeViewportScrollEnd);
    this.stage?.addEventListener('pointerenter', this.onStagePointerEnter);
    this.stage?.addEventListener('pointermove', this.onStagePointerMove);
    this.stage?.addEventListener('pointerleave', this.onStagePointerLeave);
    this.setupInlineVideos();
    this.syncViewportMode();
    this.updateControls(this.activeIndex);
    this.updateActiveMediaControls();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeyDown);
    this.removeEventListener('focusin', this.onFocusIn);
    this.removeEventListener('pointerdown', this.onPointerDown);
    this.desktopMediaQuery.removeEventListener('change', this.onDesktopMediaChange);
    this.viewport?.removeEventListener('scroll', this.onNativeViewportScroll);
    this.viewport?.removeEventListener('scrollend', this.onNativeViewportScrollEnd);
    this.stage?.removeEventListener('pointerenter', this.onStagePointerEnter);
    this.stage?.removeEventListener('pointermove', this.onStagePointerMove);
    this.stage?.removeEventListener('pointerleave', this.onStagePointerLeave);
    this.hoverZoomPane?.remove();
    this.zoomDialog?.remove();
    this.zoomPreloadGeneration += 1;
    this.zoomRenderGeneration += 1;
    if (this.nativeScrollFrame) cancelAnimationFrame(this.nativeScrollFrame);
    if (this.nativeScrollTimer) clearTimeout(this.nativeScrollTimer);
    this.unlockZoomScroll();
  }

  onClick = (event) => {
    if (event.target.closest('[data-gallery-previous]')) {
      this.showRelative(-1);
      return;
    }
    if (event.target.closest('[data-gallery-next]')) {
      this.showRelative(1);
      return;
    }
    if (event.target.closest('.avey-product-gallery__media > img')) {
      this.openZoom();
      return;
    }

    const trigger = event.target.closest('[data-media-target]');
    if (!trigger || !this.contains(trigger)) return;
    const targetIndex = this.media.findIndex((media) => media.dataset.mediaId === trigger.dataset.mediaTarget);
    this.showMedia(trigger.dataset.mediaTarget, {
      focusThumbnail: false,
      direction: targetIndex >= this.activeIndex ? 1 : -1,
    });
  };

  onKeyDown = (event) => {
    if (!event.target.matches('.avey-product-gallery__media > img')) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.openZoom();
  };

  onPointerDown = (event) => {
    if (event.target.closest('[data-gallery-previous]')) {
      this.preloadMedia(this.activeIndex - 1);
      return;
    }

    if (event.target.closest('[data-gallery-next]')) {
      this.preloadMedia(this.activeIndex + 1);
      return;
    }

    const trigger = event.target.closest('[data-media-target]');
    if (trigger && this.contains(trigger)) {
      const targetIndex = this.media.findIndex(
        (media) => media.dataset.mediaId === trigger.dataset.mediaTarget,
      );
      this.preloadMedia(targetIndex);
      return;
    }

    if (!this.usesNativeSwipe() || !event.target.closest('.avey-product-gallery__viewport')) return;
    this.preloadAround(this.activeIndex);
    this.preloadMedia(this.activeIndex + 2);
  };

  onFocusIn = (event) => {
    if (event.target.closest('[data-gallery-previous]')) {
      this.preloadMedia(this.activeIndex - 1);
    } else if (event.target.closest('[data-gallery-next]')) {
      this.preloadMedia(this.activeIndex + 1);
    }
  };

  onDesktopMediaChange = () => {
    this.syncViewportMode();
    this.updateActiveMediaControls();
  };

  onStagePointerEnter = (event) => {
    this.preloadAround(this.activeIndex);
    if (!this.hoverZoomMediaQuery.matches) return;
    if (!this.activeMediaSupportsZoom()) {
      this.hideHoverZoom();
      return;
    }
    if (event.target.closest('[data-gallery-previous], [data-gallery-next]')) {
      this.hideHoverZoom();
      return;
    }
    this.showHoverZoom(event);
  };

  onStagePointerMove = (event) => {
    if (!this.hoverZoomMediaQuery.matches) return;
    if (!this.activeMediaSupportsZoom()) {
      this.hideHoverZoom();
      return;
    }
    if (event.target.closest('[data-gallery-previous], [data-gallery-next]')) {
      this.hideHoverZoom();
      return;
    }
    if (!this.hoverZoomPane || this.hoverZoomPane.hidden) {
      this.showHoverZoom(event);
      return;
    }
    this.updateHoverZoom(event);
  };

  onStagePointerLeave = () => {
    this.hideHoverZoom();
  };

  usesNativeSwipe() {
    return !this.desktopMediaQuery.matches;
  }

  syncViewportMode() {
    if (!this.viewport) return;

    this.nativeScrollTargetIndex = null;
    if (this.nativeScrollTimer) {
      clearTimeout(this.nativeScrollTimer);
      this.nativeScrollTimer = null;
    }

    if (this.usesNativeSwipe()) {
      this.media.forEach((media) => { media.hidden = false; });
      requestAnimationFrame(() => {
        const target = this.media[this.activeIndex];
        this.viewport.scrollTo({ left: target?.offsetLeft || 0, behavior: 'auto' });
      });
      return;
    }

    this.media.forEach((media, index) => { media.hidden = index !== this.activeIndex; });
    this.viewport.scrollTo({ left: 0, behavior: 'auto' });
  }

  onNativeViewportScroll = () => {
    if (!this.usesNativeSwipe()) return;

    if (!this.nativeScrollFrame) {
      this.nativeScrollFrame = requestAnimationFrame(() => {
        this.nativeScrollFrame = null;
        this.updateIndexFromNativeScroll();
      });
    }

    if (this.nativeScrollTimer) clearTimeout(this.nativeScrollTimer);
    this.nativeScrollTimer = setTimeout(this.onNativeViewportScrollEnd, 100);
  };

  onNativeViewportScrollEnd = () => {
    if (!this.usesNativeSwipe()) return;
    if (this.nativeScrollTimer) {
      clearTimeout(this.nativeScrollTimer);
      this.nativeScrollTimer = null;
    }
    this.nativeScrollTargetIndex = null;
    this.updateIndexFromNativeScroll();
  };

  updateIndexFromNativeScroll() {
    if (!this.viewport?.clientWidth) return;
    if (this.nativeScrollTargetIndex !== null) return;

    const targetIndex = Math.min(
      Math.max(Math.round(this.viewport.scrollLeft / this.viewport.clientWidth), 0),
      this.media.length - 1,
    );
    if (targetIndex !== this.activeIndex) {
      this.commitMediaChange(targetIndex);
    }
  }

  showRelative(direction) {
    const nextIndex = Math.min(Math.max(this.activeIndex + direction, 0), this.media.length - 1);
    if (nextIndex === this.activeIndex) return;
    this.showMedia(this.media[nextIndex].dataset.mediaId, { direction });
  }

  async showMedia(
    id,
    { focusThumbnail = false, centerThumbnail = true, direction = 1, animate = true } = {},
  ) {
    const target = this.querySelector(`[data-media-id="${CSS.escape(String(id))}"]`);
    const targetIndex = this.media.indexOf(target);
    const current = this.media[this.activeIndex];
    if (!target || target === current) return;

    if (this.usesNativeSwipe()) {
      this.showNativeMedia(targetIndex, { focusThumbnail, centerThumbnail, animate });
      return;
    }

    if (this.isTransitioning) return;

    this.hideHoverZoom();
    this.isTransitioning = true;
    await this.preloadMedia(targetIndex);
    target.hidden = false;
    this.updateThumbnailSelection(targetIndex, { focusThumbnail, centerThumbnail });

    if (animate && !this.prefersReducedMotion.matches && current?.animate && target.animate) {
      const offset = direction < 0 ? -100 : 100;
      this.stage?.classList.add('is-transitioning');
      current.classList.add('is-leaving');
      target.classList.add('is-entering');

      const options = { duration: 320, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' };
      const outgoing = current.animate(
        [{ transform: 'translate3d(0, 0, 0)' }, { transform: `translate3d(${-offset}%, 0, 0)` }],
        options,
      );
      const incoming = target.animate(
        [{ transform: `translate3d(${offset}%, 0, 0)` }, { transform: 'translate3d(0, 0, 0)' }],
        options,
      );
      await Promise.allSettled([outgoing.finished, incoming.finished]);
      current.classList.remove('is-leaving');
      target.classList.remove('is-entering');
      this.stage?.classList.remove('is-transitioning');
    }

    this.commitMediaChange(targetIndex, { updateThumbnail: false });
    this.isTransitioning = false;
  }

  showNativeMedia(
    targetIndex,
    { focusThumbnail = false, centerThumbnail = true, animate = true } = {},
  ) {
    const target = this.media[targetIndex];
    if (!target || !this.viewport) return;

    this.hideHoverZoom();
    this.preloadMedia(targetIndex);
    this.nativeScrollTargetIndex = targetIndex;
    this.commitMediaChange(targetIndex, { focusThumbnail, centerThumbnail });
    this.viewport.scrollTo({
      left: target.offsetLeft,
      behavior: animate && !this.prefersReducedMotion.matches ? 'smooth' : 'auto',
    });

    if (this.nativeScrollTimer) clearTimeout(this.nativeScrollTimer);
    this.nativeScrollTimer = setTimeout(this.onNativeViewportScrollEnd, 450);
  }

  commitMediaChange(
    targetIndex,
    { focusThumbnail = false, centerThumbnail = true, updateThumbnail = true } = {},
  ) {
    const target = this.media[targetIndex];
    if (!target) return;
    this.media.forEach((media) => {
      media.hidden = this.usesNativeSwipe() ? false : media !== target;
      if (media !== target) media.querySelectorAll('video').forEach((video) => video.pause());
    });

    if (updateThumbnail) {
      this.updateThumbnailSelection(targetIndex, { focusThumbnail, centerThumbnail });
    }
    this.activeIndex = targetIndex;
    this.updateControls(targetIndex);
    this.updateActiveMediaControls();
    this.preloadAround(targetIndex);
  }

  updateThumbnailSelection(targetIndex, { focusThumbnail = false, centerThumbnail = true } = {}) {
    const target = this.media[targetIndex];
    if (!target) return;
    const id = target.dataset.mediaId;

    this.querySelectorAll('[data-media-target]').forEach((thumbnail) => {
      const active = thumbnail.dataset.mediaTarget === String(id);
      thumbnail.classList.toggle('is-active', active);
      thumbnail.setAttribute('aria-pressed', String(active));
      if (active && focusThumbnail) thumbnail.focus({ preventScroll: true });
      if (active && centerThumbnail) this.centerThumbnail(thumbnail);
    });
  }

  preloadAround(index) {
    [index - 1, index + 1].forEach((mediaIndex) => this.preloadMedia(mediaIndex));
  }

  preloadMedia(index) {
    if (index < 0 || index >= this.media.length) return Promise.resolve();
    if (this.preloadedMedia.has(index)) return this.preloadedMedia.get(index);

    const media = this.media[index];
    const image = media.querySelector('img');
    const video = media.querySelector('video');
    const deferredVideoPoster = media.dataset.galleryVideoPoster;
    let promise = Promise.resolve();

    const hasDeferredSource = image?.hasAttribute('data-gallery-srcset');
    if (image && (hasDeferredSource || !(image.complete && image.naturalWidth > 0))) {
      promise = new Promise((resolve) => {
        const fallbackSource = image.currentSrc || image.src;
        const finish = (event) => {
          image.removeEventListener('load', finish);
          image.removeEventListener('error', finish);
          if (event?.type === 'error' && hasDeferredSource) {
            image.removeAttribute('srcset');
            image.src = fallbackSource;
          }
          resolve();
        };
        image.addEventListener('load', finish);
        image.addEventListener('error', finish);
        image.loading = 'eager';

        if (hasDeferredSource) {
          image.srcset = image.dataset.gallerySrcset;
          image.src = image.dataset.gallerySrc;
          delete image.dataset.gallerySrcset;
          delete image.dataset.gallerySrc;
        }

        if (!hasDeferredSource && image.complete && image.naturalWidth > 0) finish();
      });
    } else if (video) {
      if (video.preload === 'none') {
        video.preload = 'metadata';
        video.load();
      }

      if (deferredVideoPoster) {
        promise = new Promise((resolve) => {
          const poster = new Image();
          poster.onload = () => {
            video.poster = deferredVideoPoster;
            delete media.dataset.galleryVideoPoster;
            resolve();
          };
          poster.onerror = resolve;
          poster.src = deferredVideoPoster;
        });
      }
    }

    this.preloadedMedia.set(index, promise);
    return promise;
  }

  updateControls(index) {
    if (this.counter) this.counter.textContent = `${index + 1} / ${this.media.length}`;
    if (this.previousButton) this.previousButton.disabled = index <= 0;
    if (this.nextButton) this.nextButton.disabled = index >= this.media.length - 1;
  }

  centerThumbnail(thumbnail) {
    const rail = thumbnail.parentElement;
    if (!rail || rail.scrollWidth <= rail.clientWidth) return;
    const left = thumbnail.offsetLeft - (rail.clientWidth - thumbnail.offsetWidth) / 2;
    rail.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
  }

  getLargestImageSource(image) {
    const sourceSet = image.dataset.gallerySrcset || image.srcset || '';
    const candidates = sourceSet
      .split(',')
      .map((candidate) => {
        const [url, descriptor = '0w'] = candidate.trim().split(/\s+/);
        return { url, width: Number.parseInt(descriptor, 10) || 0 };
      })
      .filter((candidate) => candidate.url)
      .sort((a, b) => a.width - b.width);
    return candidates.at(-1)?.url || image.dataset.gallerySrc || image.currentSrc || image.src;
  }

  getVideoPlayLabel() {
    const language = document.documentElement.lang.split('-')[0];
    const labels = {
      en: 'Play video',
      es: 'Reproducir vídeo',
      fr: 'Lire la vidéo',
    };
    return labels[language] || labels.fr;
  }

  createVideoPlayButton(video, className) {
    video.controls = false;
    video.playsInline = true;
    video.removeAttribute('autoplay');

    const playButton = document.createElement('button');
    playButton.type = 'button';
    playButton.className = className;
    playButton.setAttribute('aria-label', this.getVideoPlayLabel());
    playButton.hidden = true;
    playButton.innerHTML = '<span aria-hidden="true"></span>';

    const syncPlayButton = () => {
      const isReady = video.readyState >= HTMLMediaElement.HAVE_METADATA || Boolean(video.poster);
      playButton.hidden = !isReady || (!video.paused && !video.ended);
      if (!playButton.hidden) playButton.disabled = false;
    };

    playButton.addEventListener('click', () => {
      playButton.disabled = true;
      video.controls = true;
      video.play().catch(() => {
        video.controls = false;
        playButton.disabled = false;
        syncPlayButton();
      });
    });
    video.addEventListener('loadedmetadata', syncPlayButton);
    video.addEventListener('loadeddata', syncPlayButton);
    video.addEventListener('canplay', syncPlayButton);
    video.addEventListener('play', () => {
      video.controls = true;
      syncPlayButton();
    });
    video.addEventListener('pause', syncPlayButton);
    video.addEventListener('ended', syncPlayButton);

    syncPlayButton();
    return playButton;
  }

  setupInlineVideos() {
    this.media.forEach((media) => {
      const video = media.querySelector('video');
      if (!video || media.querySelector('[data-inline-video-play]')) return;
      const playButton = this.createVideoPlayButton(
        video,
        'avey-product-gallery__video-play-button',
      );
      playButton.dataset.inlineVideoPlay = '';
      media.append(playButton);
    });
  }

  activeMediaSupportsZoom() {
    return Boolean(this.media[this.activeIndex]?.querySelector(':scope > img'));
  }

  updateActiveMediaControls() {
    const supportsZoom = this.activeMediaSupportsZoom();
    const keyboardZoomAvailable = supportsZoom && this.desktopMediaQuery.matches;

    this.media.forEach((media, index) => {
      const image = media.querySelector(':scope > img');
      if (!image) return;
      const isActive = index === this.activeIndex && keyboardZoomAvailable;
      image.tabIndex = isActive ? 0 : -1;
      if (isActive) {
        image.setAttribute('role', 'button');
        image.setAttribute('aria-label', this.dataset.galleryExpandLabel || '');
      } else {
        image.removeAttribute('role');
        image.removeAttribute('aria-label');
      }
    });

    if (!supportsZoom) this.hideHoverZoom();
  }

  collectZoomItems() {
    return this.media
      .map((media) => {
        const image = media.querySelector(':scope > img');
        const playableMedia = media.querySelector('video, iframe, model-viewer');
        if (!image && !playableMedia) return null;

        const id = String(media.dataset.mediaId);
        const thumbnail = this.querySelector(
          `[data-media-target="${CSS.escape(id)}"] img`,
        );

        if (image) {
          return {
            id,
            type: 'image',
            source: this.getLargestImageSource(image),
            thumbnail: thumbnail?.currentSrc || thumbnail?.src || image.currentSrc || image.src,
            alt: image.alt || '',
          };
        }

        return {
          id,
          type: playableMedia.tagName.toLowerCase(),
          element: playableMedia,
          thumbnail: thumbnail?.currentSrc || thumbnail?.src || '',
          alt: playableMedia.getAttribute('aria-label') || this.dataset.productTitle || '',
        };
      })
      .filter(Boolean);
  }

  buildZoomDialog() {
    const dialog = document.createElement('dialog');
    dialog.className = 'avey-product-zoom';
    dialog.setAttribute('aria-label', this.dataset.galleryDialogLabel || '');

    const shell = document.createElement('div');
    shell.className = 'avey-product-zoom__shell';

    const closeButton = document.createElement('button');
    closeButton.className = 'avey-product-zoom__close';
    closeButton.type = 'button';
    closeButton.dataset.zoomClose = '';
    closeButton.setAttribute('aria-label', this.dataset.galleryCloseLabel || 'Fermer');
    closeButton.innerHTML = '<span aria-hidden="true">×</span>';

    const layout = document.createElement('div');
    layout.className = 'avey-product-zoom__layout';

    const sidebar = document.createElement('aside');
    sidebar.className = 'avey-product-zoom__sidebar';

    const productTitle = document.createElement('h2');
    productTitle.className = 'avey-product-zoom__title';
    productTitle.textContent = this.dataset.productTitle || '';

    const thumbnails = document.createElement('div');
    thumbnails.className = 'avey-product-zoom__thumbnails';
    thumbnails.setAttribute('role', 'list');

    this.zoomItems.forEach((item, index) => {
      const button = document.createElement('button');
      button.className = 'avey-product-zoom__thumbnail';
      button.type = 'button';
      button.dataset.zoomIndex = String(index);
      button.setAttribute('role', 'listitem');
      const mediaLabel = item.type === 'image' ? 'Image' : 'Vidéo';
      button.setAttribute('aria-label', item.alt || `${mediaLabel} ${index + 1}`);

      const image = document.createElement('img');
      image.src = item.thumbnail;
      image.alt = '';
      image.width = 88;
      image.height = 88;
      image.loading = 'lazy';
      button.append(image);

      if (item.type !== 'image') {
        const playIcon = document.createElement('span');
        playIcon.className = 'avey-product-gallery__play';
        playIcon.setAttribute('aria-hidden', 'true');
        playIcon.textContent = '▶';
        button.append(playIcon);
      }

      thumbnails.append(button);
    });

    const viewer = document.createElement('div');
    viewer.className = 'avey-product-zoom__viewer';

    const content = document.createElement('div');
    content.className = 'avey-product-zoom__content';
    content.dataset.zoomContent = '';

    const previousButton = document.createElement('button');
    previousButton.className = 'avey-product-zoom__arrow avey-product-zoom__arrow--previous';
    previousButton.type = 'button';
    previousButton.dataset.zoomPrevious = '';
    previousButton.setAttribute('aria-label', this.dataset.galleryPreviousLabel || 'Image précédente');
    previousButton.innerHTML = '<span aria-hidden="true">←</span>';

    const nextButton = document.createElement('button');
    nextButton.className = 'avey-product-zoom__arrow avey-product-zoom__arrow--next';
    nextButton.type = 'button';
    nextButton.dataset.zoomNext = '';
    nextButton.setAttribute('aria-label', this.dataset.galleryNextLabel || 'Image suivante');
    nextButton.innerHTML = '<span aria-hidden="true">→</span>';

    const counter = document.createElement('span');
    counter.className = 'avey-product-zoom__counter';
    counter.dataset.zoomCounter = '';
    counter.setAttribute('aria-live', 'polite');

    viewer.append(content, previousButton, nextButton, counter);
    sidebar.append(productTitle, thumbnails);
    layout.append(viewer, sidebar);
    shell.append(closeButton, layout);
    dialog.append(shell);
    document.body.append(dialog);

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog || event.target.closest('[data-zoom-close]')) {
        dialog.close();
        return;
      }
      if (event.target.closest('[data-zoom-previous]')) {
        this.renderZoom(this.zoomIndex - 1);
        return;
      }
      if (event.target.closest('[data-zoom-next]')) {
        this.renderZoom(this.zoomIndex + 1);
        return;
      }
      const zoomImage = event.target.closest('[data-zoom-image]');
      if (zoomImage) {
        this.toggleDialogImageZoom(event, zoomImage);
        return;
      }
      const thumbnail = event.target.closest('[data-zoom-index]');
      if (thumbnail) this.renderZoom(Number(thumbnail.dataset.zoomIndex));
    });

    dialog.addEventListener('pointermove', (event) => {
      const zoomImage = event.target.closest('[data-zoom-image].is-zoomed');
      if (zoomImage) this.updateDialogImageZoom(event, zoomImage);
    });

    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.renderZoom(this.zoomIndex - 1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.renderZoom(this.zoomIndex + 1);
      }
    });

    dialog.addEventListener('close', () => {
      this.zoomPreloadGeneration += 1;
      this.zoomRenderGeneration += 1;
      dialog.querySelectorAll('video').forEach((video) => video.pause());
      this.resetDialogImageZoom();
      this.unlockZoomScroll();
      const selected = this.zoomItems[this.zoomIndex];
      if (selected) {
        const selectedIndex = this.media.findIndex((media) => String(media.dataset.mediaId) === selected.id);
        this.showMedia(selected.id, { direction: selectedIndex >= this.activeIndex ? 1 : -1 });
      }
    });

    this.zoomDialog = dialog;
  }

  preloadZoomImage(item, priority = 'low') {
    if (item?.type !== 'image' || !item.source) return Promise.resolve(false);

    const existing = this.zoomPreloads.get(item.source);
    if (existing) {
      if (priority === 'high') existing.image.fetchPriority = 'high';
      return existing.promise;
    }

    const preload = new Image();
    preload.fetchPriority = priority;
    const promise = new Promise((resolve) => {
      preload.onload = () => resolve(true);
      preload.onerror = () => {
        this.zoomPreloads.delete(item.source);
        resolve(false);
      };
      preload.src = item.source;
    });

    this.zoomPreloads.set(item.source, { image: preload, promise });
    return promise;
  }

  scheduleZoomPreloads(index) {
    const generation = ++this.zoomPreloadGeneration;
    const current = this.zoomItems[index];
    const adjacentIndexes = [index - 1, index + 1].filter(
      (itemIndex) => itemIndex >= 0 && itemIndex < this.zoomItems.length,
    );
    const remainingIndexes = this.zoomItems
      .map((item, itemIndex) => ({ item, itemIndex }))
      .filter(
        ({ item, itemIndex }) =>
          item.type === 'image'
          && itemIndex !== index
          && !adjacentIndexes.includes(itemIndex),
      )
      .sort((a, b) => Math.abs(a.itemIndex - index) - Math.abs(b.itemIndex - index));

    const preloadInOrder = async () => {
      await this.preloadZoomImage(current, 'high');
      if (generation !== this.zoomPreloadGeneration || !this.zoomDialog?.open) return;

      await Promise.all(
        adjacentIndexes.map((itemIndex) => this.preloadZoomImage(this.zoomItems[itemIndex], 'high')),
      );
      if (generation !== this.zoomPreloadGeneration || !this.zoomDialog?.open) return;

      for (const { item } of remainingIndexes) {
        if (generation !== this.zoomPreloadGeneration || !this.zoomDialog?.open) return;
        await this.preloadZoomImage(item);
      }
    };

    void preloadInOrder();
  }

  renderZoom(index) {
    if (!this.zoomDialog || !this.zoomItems.length) return;
    this.zoomIndex = Math.min(Math.max(index, 0), this.zoomItems.length - 1);
    const item = this.zoomItems[this.zoomIndex];
    const content = this.zoomDialog.querySelector('[data-zoom-content]');
    const previousButton = this.zoomDialog.querySelector('[data-zoom-previous]');
    const nextButton = this.zoomDialog.querySelector('[data-zoom-next]');
    const counter = this.zoomDialog.querySelector('[data-zoom-counter]');

    content.querySelectorAll('video').forEach((video) => video.pause());
    content.replaceChildren();
    this.resetDialogImageZoom();

    if (item.type === 'image') {
      const renderGeneration = ++this.zoomRenderGeneration;
      const image = document.createElement('img');
      image.className = 'avey-product-zoom__image';
      image.dataset.zoomImage = '';
      image.src = item.thumbnail || item.source;
      image.alt = item.alt;
      image.draggable = false;
      content.append(image);

      this.preloadZoomImage(item, 'high').then((loaded) => {
        if (
          !loaded
          || renderGeneration !== this.zoomRenderGeneration
          || !image.isConnected
        ) return;
        image.src = item.source;
      });
    } else {
      this.zoomRenderGeneration += 1;
      const mediaElement = item.element.cloneNode(true);
      mediaElement.removeAttribute('id');
      mediaElement.classList.add('avey-product-zoom__media');

      if (mediaElement instanceof HTMLVideoElement) {
        mediaElement.preload = 'metadata';
        const playButton = this.createVideoPlayButton(
          mediaElement,
          'avey-product-zoom__play-button',
        );
        content.append(mediaElement, playButton);
      } else {
        if (mediaElement instanceof HTMLIFrameElement) {
          mediaElement.setAttribute('allowfullscreen', '');
        }
        content.append(mediaElement);
      }
    }
    previousButton.disabled = this.zoomIndex === 0;
    nextButton.disabled = this.zoomIndex === this.zoomItems.length - 1;
    counter.textContent = `${this.zoomIndex + 1} / ${this.zoomItems.length}`;

    this.zoomDialog.querySelectorAll('[data-zoom-index]').forEach((thumbnail, thumbnailIndex) => {
      const active = thumbnailIndex === this.zoomIndex;
      thumbnail.classList.toggle('is-active', active);
      if (active) {
        thumbnail.setAttribute('aria-current', 'true');
        thumbnail.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      } else {
        thumbnail.removeAttribute('aria-current');
      }
    });

    if (this.zoomDialog.open) this.scheduleZoomPreloads(this.zoomIndex);
  }

  toggleDialogImageZoom(event, image) {
    if (image.classList.contains('is-zoomed')) {
      this.resetDialogImageZoom();
      return;
    }

    image.classList.add('is-zoomed');
    image.setAttribute('aria-pressed', 'true');
    this.updateDialogImageZoom(event, image);
  }

  updateDialogImageZoom(event, image) {
    const rect = image.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const x = Math.min(Math.max(((event.clientX - rect.left) / rect.width) * 100, 0), 100);
    const y = Math.min(Math.max(((event.clientY - rect.top) / rect.height) * 100, 0), 100);
    image.style.transformOrigin = `${x}% ${y}%`;
  }

  resetDialogImageZoom() {
    const image = this.zoomDialog?.querySelector('[data-zoom-image]');
    if (!image) return;
    image.classList.remove('is-zoomed');
    image.removeAttribute('aria-pressed');
    image.style.removeProperty('transform-origin');
  }

  ensureHoverZoom() {
    if (this.hoverZoomPane && this.hoverZoomLens) return;

    const pane = document.createElement('div');
    pane.className = 'avey-product-gallery__hover-zoom';
    pane.hidden = true;
    pane.setAttribute('aria-hidden', 'true');

    const lens = document.createElement('div');
    lens.className = 'avey-product-gallery__zoom-lens';
    lens.hidden = true;
    lens.setAttribute('aria-hidden', 'true');

    document.body.append(pane);
    this.stage?.append(lens);
    this.hoverZoomPane = pane;
    this.hoverZoomLens = lens;
  }

  showHoverZoom(event) {
    const activeMedia = this.media[this.activeIndex];
    const image = activeMedia?.querySelector('img');
    const purchaseColumn = this.closest('avey-product')?.querySelector('.avey-product__info');
    if (!image || !purchaseColumn || !this.stage) return;

    this.ensureHoverZoom();
    const stageRect = this.stage.getBoundingClientRect();
    const purchaseRect = purchaseColumn.getBoundingClientRect();
    const paneLeft = stageRect.right + 16;
    const paneWidth = Math.max(
      320,
      Math.min(purchaseRect.right - paneLeft, window.innerWidth - paneLeft - 16),
    );

    this.hoverZoomPane.style.top = `${Math.round(stageRect.top)}px`;
    this.hoverZoomPane.style.left = `${Math.round(paneLeft)}px`;
    this.hoverZoomPane.style.width = `${Math.round(paneWidth)}px`;
    this.hoverZoomPane.style.height = `${Math.round(stageRect.height)}px`;
    const source = this.getLargestImageSource(image).replaceAll('"', '%22');
    this.hoverZoomPane.style.backgroundImage = `url("${source}")`;
    this.hoverZoomPane.hidden = false;
    this.hoverZoomLens.hidden = false;
    this.updateHoverZoom(event);
  }

  updateHoverZoom(event) {
    if (!this.stage || !this.hoverZoomPane || !this.hoverZoomLens) return;
    const stageRect = this.stage.getBoundingClientRect();
    const paneRect = this.hoverZoomPane.getBoundingClientRect();
    const zoom = 2;
    const cursorX = Math.min(Math.max(event.clientX - stageRect.left, 0), stageRect.width);
    const cursorY = Math.min(Math.max(event.clientY - stageRect.top, 0), stageRect.height);
    const lensWidth = Math.min(stageRect.width, paneRect.width / zoom);
    const lensHeight = Math.min(stageRect.height, paneRect.height / zoom);
    const lensLeft = Math.min(Math.max(cursorX - lensWidth / 2, 0), stageRect.width - lensWidth);
    const lensTop = Math.min(Math.max(cursorY - lensHeight / 2, 0), stageRect.height - lensHeight);

    this.hoverZoomLens.style.width = `${lensWidth}px`;
    this.hoverZoomLens.style.height = `${lensHeight}px`;
    this.hoverZoomLens.style.transform = `translate3d(${lensLeft}px, ${lensTop}px, 0)`;

    const backgroundWidth = stageRect.width * zoom;
    const backgroundHeight = stageRect.height * zoom;
    const backgroundX = Math.min(
      0,
      Math.max(paneRect.width - backgroundWidth, paneRect.width / 2 - cursorX * zoom),
    );
    const backgroundY = Math.min(
      0,
      Math.max(paneRect.height - backgroundHeight, paneRect.height / 2 - cursorY * zoom),
    );
    this.hoverZoomPane.style.backgroundSize = `${backgroundWidth}px ${backgroundHeight}px`;
    this.hoverZoomPane.style.backgroundPosition = `${backgroundX}px ${backgroundY}px`;
  }

  hideHoverZoom() {
    if (this.hoverZoomPane) this.hoverZoomPane.hidden = true;
    if (this.hoverZoomLens) this.hoverZoomLens.hidden = true;
  }

  lockZoomScroll() {
    if (this.zoomScrollY !== undefined) return;
    this.zoomScrollY = window.scrollY;
    this.zoomPreviousBodyTop = document.body.style.top;
    document.body.style.top = `-${this.zoomScrollY}px`;
    document.documentElement.classList.add('avey-zoom-open');
  }

  unlockZoomScroll() {
    document.documentElement.classList.remove('avey-zoom-open');
    if (this.zoomScrollY === undefined) return;
    const scrollY = this.zoomScrollY;
    if (this.zoomPreviousBodyTop) {
      document.body.style.top = this.zoomPreviousBodyTop;
    } else {
      document.body.style.removeProperty('top');
    }
    this.zoomScrollY = undefined;
    this.zoomPreviousBodyTop = undefined;
    window.scrollTo(0, scrollY);
  }

  openZoom() {
    if (!this.desktopMediaQuery.matches || !this.activeMediaSupportsZoom()) return;
    this.hideHoverZoom();
    this.zoomItems = this.collectZoomItems();
    if (!this.zoomItems.length) return;
    if (!this.zoomDialog) this.buildZoomDialog();

    const activeId = String(this.media[this.activeIndex]?.dataset.mediaId || '');
    const activeZoomIndex = this.zoomItems.findIndex((item) => item.id === activeId);
    this.renderZoom(activeZoomIndex >= 0 ? activeZoomIndex : 0);
    if (!this.zoomDialog.open) {
      this.lockZoomScroll();
      this.zoomDialog.showModal();
    }
    this.scheduleZoomPreloads(this.zoomIndex);
  }
}
